/**
 * Google Analytics (GA4) — raw website traffic and referrers only.
 *
 * Privacy: we send a sanitized path (no query string, no hash, private link
 * tokens and record IDs replaced with ":id") so share tokens, invite codes and
 * company/listing IDs never reach Google. No user IDs or names are sent.
 * Admin pages are never tracked. Only runs on the production domains.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { GA_MEASUREMENT_ID } from "./googleAnalyticsId";

declare global {
  interface Window { dataLayer: unknown[]; gtag?: (...args: unknown[]) => void }
}

const PROD_HOSTS = new Set(["cannafax.ai", "www.cannafax.ai", "cannafax-ai.lovable.app"]);
const STATIC_CHILD = new Set(["edit", "listing", "new"]);
const ID_LIKE = /^[0-9a-f-]{16,}$|\d|^[A-Za-z0-9_-]{12,}$/;

export function sanitizeAnalyticsPath(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  return "/" + parts.map((seg, i) => (i > 0 && !STATIC_CHILD.has(seg) && (ID_LIKE.test(seg) || i >= 1 && /^(s|r|j|share|shared|menu-invite|menu-access-request)$/.test(parts[0])) ? ":id" : seg)).join("/");
}

let initialized = false;
function init(): boolean {
  if (initialized) return true;
  if (typeof window === "undefined" || !PROD_HOSTS.has(window.location.hostname)) return false;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params -- gtag.js needs the Arguments object
    window.dataLayer.push(arguments);
  };
  window.gtag("set", "developer_id.dZjgwMW", true);
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  initialized = true;
  return true;
}

export function GoogleAnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin") || !init()) return;
    const path = sanitizeAnalyticsPath(pathname);
    window.gtag?.("event", "page_view", {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: document.title,
    });
  }, [pathname]);
  return null;
}
